/* eslint-disable camelcase */
import { z } from 'zod'

import type {
  SummaryChipCohorts as ApiCohorts,
  SummaryChipGroupedTrend as ApiTrend,
  SummaryChipOverview as ApiOverview,
  SummaryChipSource as ApiSource,
  SummaryChipSummary as ApiCounts,
} from './generated/api'
import { type AttributionDimension, attributionDimensions, type AttributionFilters } from './attributionObservability'
import { fetchAPI } from '.'

export const summaryChipSources = ['publication', 'current_state'] as const satisfies readonly ApiSource[]
export type SummaryChipSource = (typeof summaryChipSources)[number]

const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)
export const summaryChipCountsSchema = z.object({
  measured_at_min: z.string().datetime({ offset: true }).nullable().default(null),
  measured_at_max: z.string().datetime({ offset: true }).nullable().default(null),
  meeting_count: count,
  measured_meetings: count,
  eligible_meetings: count,
  zero_chip_meetings: count,
  unmeasured_meetings: count,
  speaker_chip_count: count,
  named_speaker_chip_count: count,
}) satisfies z.ZodType<ApiCounts>
const metadata = z.object({
  source: z.enum(summaryChipSources).default('publication'),
  status: z.enum(['available', 'unsupported']),
  unsupported_dimensions: z.array(z.string()),
  supported_dimensions: z.array(z.enum(attributionDimensions)),
  population: z.literal('consented_individual_published_meetings'),
})
const point = z.object({ date: z.string(), summary: summaryChipCountsSchema })
export const summaryChipOverviewSchema = metadata.extend({
  summary: summaryChipCountsSchema,
  trend: z.array(point),
}) satisfies z.ZodType<ApiOverview>
const cohortsSchema = metadata.extend({
  groups: z.array(z.object({ value: z.string(), summary: summaryChipCountsSchema })),
  truncated: z.boolean(),
}) satisfies z.ZodType<ApiCohorts>
const trendSchema = metadata.extend({
  group_by: z.array(z.enum(attributionDimensions)),
  groups: z.array(
    z.object({
      path: z.array(z.object({ dimension: z.enum(attributionDimensions), value: z.string() })),
      trend: z.array(point),
    })
  ),
  truncated: z.boolean(),
}) satisfies z.ZodType<ApiTrend>
export type SummaryChipCounts = z.infer<typeof summaryChipCountsSchema>
export type SummaryChipOverview = z.infer<typeof summaryChipOverviewSchema>
export type SummaryChipCohorts = z.infer<typeof cohortsSchema>
export type SummaryChipTrend = z.infer<typeof trendSchema>

const BASE_PATH = '/api/v1/internal/meeting-speaker-labels/summary-chips'
const read = async <T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  params: object,
  signal: AbortSignal | undefined,
  source: SummaryChipSource
): Promise<z.infer<T>> => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE_PATH}${path}`,
    params: { ...params, source },
    paramsSerializer: { indexes: null },
    signal,
    expectedStatuses: [404, 405],
  })
  const result = schema.parse(response.data)
  if (metadata.parse(response.data).source !== source)
    throw new Error('The backend returned a different summary-chip source.')
  return result
}
export const getSummaryChipOverview = (
  filters: AttributionFilters,
  signal?: AbortSignal,
  source: SummaryChipSource = 'publication'
) => read(summaryChipOverviewSchema, '', filters, signal, source)
export const getSummaryChipCohorts = (
  filters: AttributionFilters,
  groupBy: AttributionDimension,
  signal?: AbortSignal,
  source: SummaryChipSource = 'publication'
) => read(cohortsSchema, '/cohorts', { ...filters, group_by: groupBy }, signal, source)
export const getSummaryChipTrend = (
  filters: AttributionFilters,
  axes: AttributionDimension[],
  signal?: AbortSignal,
  source: SummaryChipSource = 'publication'
) => read(trendSchema, '/trend', { ...filters, group_by: axes }, signal, source)
